/**
 * "Email the coaches at your schools" from the game plan (APS-259).
 *
 * Lists the athlete's saved (hearted) schools, picked up front, and opens the
 * bulk composer (BulkEmailCoachModal) on every coach at each picked program.
 * Free programs work as everywhere else: a program already held costs
 * nothing, a new one is named in a confirm before it is spent, and a family
 * out of programs gets the upgrade modal. House schools never show.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, X } from 'react-feather';
import Modal from '../Modal';
import BulkEmailCoachModal from '../BulkEmailCoachModal';
import { Button, Skeleton } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useConfirm } from '../../hooks/useConfirm';
import { coachUnlocksAPI } from '../../services/api';
import { schoolListsAPI } from '../../services/schoolListsAPI';
import analytics from '../../services/analytics';
import {
  BIG_SEND,
  ProgramRow,
  Recipient,
  SavedSchool,
  UnlockState,
  canPickAnother,
  confirmCopy,
  costsProgram,
  dedupeRecipients,
  fitSelection,
  initialSelection,
  joinNames,
  listedRecipients,
  newPrograms,
  programRows,
  recipientsFor,
  unlockStateFrom,
} from './emailPrograms';
import {
  CopyCtx,
  PROGRAMS_CHANGED,
  PROGRAMS_EMPTY,
  PROGRAMS_EVERY_COACH,
  PROGRAMS_GMAIL,
  PROGRAMS_OUT,
  PROGRAMS_STATUS_FAILED,
  PROGRAMS_SWAP,
  PROGRAMS_SWAP_TOAST,
  contactTitle,
  programsBigSendLine,
  programsBlockedToast,
  programsButton,
  programsFreeLine,
  programsNoEmailLine,
} from './gamePlanCopy';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string;
  ctx: CopyCtx;
  /** From the game plan's Gmail step. */
  gmailConnected: boolean;
  /** "Explore schools" when the list is empty. */
  onPickSchools: () => void;
  /** After the composer closes, so the caller can reload the plan. */
  onDone: () => void;
}

type Load = 'loading' | 'ready' | 'error';

const EmailProgramsModal: React.FC<Props> = ({ isOpen, onClose, athleteId, ctx, gmailConnected, onPickSchools, onDone }) => {
  const { sessionToken } = useAuth();
  const { setModalAction } = useSubscription();
  const navigate = useNavigate();
  const toast = useToast();
  const [load, setLoad] = useState<Load>('loading');
  const [rows, setRows] = useState<ProgramRow[]>([]);
  const [noEmail, setNoEmail] = useState(0);
  const [state, setState] = useState<UnlockState>({ isPaid: false, remaining: 0, unlocked: new Set() });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<Recipient[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const inFlight = useRef(false);

  const fresh = useMemo(() => newPrograms(selected, rows, state), [selected, rows, state]);
  const confirmText = confirmCopy(fresh, state.remaining);
  const { ConfirmComponent: SpendConfirm, confirm: confirmSpend } = useConfirm({
    title: confirmText.title,
    message: confirmText.message,
    confirmText: 'Unlock and write',
    cancelText: 'Cancel',
    variant: 'info',
  });

  useEffect(() => {
    if (!isOpen || !sessionToken || !athleteId) return undefined;
    let alive = true;
    setLoad('loading');
    setError(null);
    setRecipients(null);
    Promise.all([
      schoolListsAPI.getSchoolLists(sessionToken, athleteId),
      coachUnlocksAPI.getStatus(sessionToken, athleteId),
    ])
      .then(([lists, status]) => {
        if (!alive) return;
        const next = unlockStateFrom(status);
        if (!next) {
          setLoad('error');
          return;
        }
        const saved: SavedSchool[] = [
          ...(lists.data?.dreamSchools || []),
          ...(lists.data?.targetSchools || []),
          ...(lists.data?.safetySchools || []),
        ] as unknown as SavedSchool[];
        const built = programRows(saved);
        setRows(built.rows);
        setNoEmail(built.noEmail);
        setState(next);
        setSelected(initialSelection(built.rows, next));
        setLoad('ready');
        try {
          analytics.track('game_plan_programs_opened', {
            schools: built.rows.length,
            is_paid: next.isPaid,
            remaining: next.remaining,
          });
        } catch {}
      })
      .catch(() => { if (alive) setLoad('error'); });
    return () => { alive = false; };
  }, [isOpen, sessionToken, athleteId, reloadKey]);

  const outOfPrograms = !state.isPaid && state.remaining <= 0;
  const coachCount = rows.filter((r) => selected.has(r.school_id)).reduce((n, r) => n + r.emailCount, 0);

  const showUpgrade = (label: string) => {
    try { analytics.track('game_plan_programs_upgrade_shown', {}); } catch {}
    setModalAction(label);
  };

  const toggle = (row: ProgramRow) => {
    if (working) return;
    const next = new Set(selected);
    if (next.has(row.school_id)) {
      next.delete(row.school_id);
    } else {
      if (costsProgram(row.school_id, state) && !canPickAnother(selected, rows, state)) {
        if (outOfPrograms) showUpgrade(`Email coaches at ${row.name}`);
        else toast.info(PROGRAMS_SWAP_TOAST);
        return;
      }
      next.add(row.school_id);
    }
    setSelected(next);
    setError(null);
  };

  const write = async () => {
    if (!gmailConnected) {
      onClose();
      navigate('/settings/connections');
      return;
    }
    if (inFlight.current || !sessionToken || selected.size === 0) return;
    inFlight.current = true;
    setError(null);

    // Read the free count again before a spend, so the confirm is current.
    let cur = state;
    const picks = selected;
    if (!cur.isPaid && newPrograms(picks, rows, cur).length > 0) {
      setWorking(true);
      const st = unlockStateFrom(await coachUnlocksAPI.getStatus(sessionToken, athleteId).catch(() => null));
      setWorking(false);
      if (!st) {
        inFlight.current = false;
        setError(PROGRAMS_STATUS_FAILED);
        return;
      }
      cur = st;
      setState(st);
      const fitted = fitSelection(picks, rows, st);
      if (fitted.size !== picks.size) {
        inFlight.current = false;
        setSelected(fitted);
        setError(PROGRAMS_CHANGED);
        return;
      }
    }
    const picked = rows.filter((r) => picks.has(r.school_id));
    const spending = newPrograms(picks, rows, cur);
    if (spending.length > 0) {
      const ok = await confirmSpend();
      if (!ok) {
        inFlight.current = false;
        return;
      }
    }
    setWorking(true);
    const gathered: Recipient[] = [];
    const unlockedNow = new Set(cur.unlocked);
    let remaining = cur.remaining;
    const blocked: ProgramRow[] = [];
    const failed: ProgramRow[] = [];
    try {
      // Programs that cost nothing use the staff the lists call sent. Only a
      // new program, or a held one with a hidden coach, asks the server. One
      // at a time, so the server's free count is read fresh each call.
      for (const row of picked) {
        const listed = listedRecipients(row, cur);
        if (listed) {
          gathered.push(...listed);
          continue;
        }
        const costs = costsProgram(row.school_id, cur);
        if (costs && blocked.length > 0) {
          blocked.push(row);
          continue;
        }
        const res = await coachUnlocksAPI.unlockSchool(sessionToken, row.school_id, athleteId).catch(() => null);
        if (res && (res.status === 402 || res.paywall)) {
          blocked.push(row);
          remaining = 0;
          continue;
        }
        if (!res || res.status !== 200 || !res.unlocked) {
          failed.push(row);
          continue;
        }
        if (!cur.isPaid) unlockedNow.add(String(row.school_id));
        if (typeof res.remaining === 'number') remaining = res.remaining;
        gathered.push(...recipientsFor(row, res.coaches));
      }
    } finally {
      inFlight.current = false;
      setWorking(false);
    }

    if (!cur.isPaid) setState({ isPaid: false, remaining: Math.max(0, remaining), unlocked: unlockedNow });
    if (blocked.length > 0) {
      const blockedIds = new Set(blocked.map((r) => r.school_id));
      setSelected((prev) => new Set(Array.from(prev).filter((id) => !blockedIds.has(id))));
    }

    const list = dedupeRecipients(gathered);
    try {
      analytics.track('game_plan_programs_write', {
        schools: picked.length,
        new_programs: spending.length,
        coaches: list.length,
        failed: failed.length,
        blocked: blocked.length,
      });
    } catch {}

    if (list.length === 0 && blocked.length > 0) {
      showUpgrade(`Email coaches at ${blocked[0].name}`);
      return;
    }
    if (failed.length > 0 && list.length === 0) {
      setError('We could not open these schools. Try again.');
      return;
    }
    if (list.length === 0) {
      setError('We have no coach emails on file for these schools yet.');
      return;
    }
    if (blocked.length > 0) {
      toast.warning(programsBlockedToast(blocked.map((r) => r.name)));
    }
    if (failed.length > 0) {
      toast.warning(`We could not open ${joinNames(failed.map((r) => r.name))}. The rest are ready.`);
    }
    setRecipients(list);
  };

  const closeComposer = () => {
    setRecipients(null);
    onClose();
    onDone();
  };

  // The composer replaces the picker, so only one dialog is up at a time.
  if (recipients) {
    return (
      <BulkEmailCoachModal
        isOpen
        onClose={closeComposer}
        coaches={recipients}
        athleteProfileId={athleteId}
        onRemoveCoach={(email) => setRecipients((prev) => (prev || []).filter((r) => r.email !== email))}
      />
    );
  }

  const freeLine = programsFreeLine({ is_paid: state.isPaid, remaining: state.remaining }, ctx);
  const noEmailLine = programsNoEmailLine(noEmail);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        disableClose={working}
        labelledById="email-programs-title"
        contentStyle={{ maxWidth: 520, fontFamily: 'var(--aps-font-sans)' }}
      >
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 id="email-programs-title" style={{ margin: 0, fontSize: 19, fontWeight: 800, color: 'var(--aps-ink)' }}>
                {contactTitle(ctx)}
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--aps-text-2)' }}>
                {PROGRAMS_EVERY_COACH}
                {freeLine && load === 'ready' ? ` ${freeLine}` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={working}
              aria-label="Close"
              style={{
                width: 34, height: 34, flexShrink: 0, borderRadius: 999, border: 'none',
                background: 'var(--aps-fill)', color: 'var(--aps-text-muted)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {load === 'loading' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-busy="true">
              <Skeleton height={48} />
              <Skeleton height={48} />
              <Skeleton height={48} />
            </div>
          )}

          {load === 'error' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
              <p role="alert" style={{ margin: 0, fontSize: 14, color: 'var(--aps-text-2)' }}>
                We could not load your schools.
              </p>
              <Button variant="secondary" size="sm" onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>
            </div>
          )}

          {load === 'ready' && rows.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--aps-text-2)' }}>
                {noEmailLine || PROGRAMS_EMPTY}
              </p>
              <Button variant="primary" size="sm" onClick={() => { onClose(); onPickSchools(); }}>
                Explore schools
              </Button>
            </div>
          )}

          {load === 'ready' && rows.length > 0 && (
            <>
              <ul
                aria-label="Schools on your list"
                style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '45vh', overflowY: 'auto' }}
              >
                {rows.map((row) => {
                  const on = selected.has(row.school_id);
                  const costs = costsProgram(row.school_id, state);
                  const full = costs && !on && !canPickAnother(selected, rows, state);
                  const locked = full && outOfPrograms;
                  let note: string | null = null;
                  if (!state.isPaid && costs) {
                    if (!full) note = 'Uses 1 free program';
                    else note = locked ? 'Needs a plan' : PROGRAMS_SWAP;
                  }
                  return (
                    <li key={row.school_id}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => toggle(row)}
                        disabled={working}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 12px', borderRadius: 12, cursor: working ? 'default' : 'pointer',
                          textAlign: 'left', fontFamily: 'inherit',
                          background: on ? 'var(--aps-action-tint)' : 'var(--aps-surface)',
                          border: `1px solid ${on ? 'var(--aps-action-border)' : 'var(--aps-border)'}`,
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 20, height: 20, flexShrink: 0, borderRadius: 6,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: on ? 'var(--aps-action)' : 'var(--aps-surface)',
                            border: on ? 'none' : '1.5px solid var(--aps-border-strong)',
                            color: 'var(--aps-on-ink)', boxSizing: 'border-box',
                          }}
                        >
                          {on && <Check size={14} />}
                        </span>
                        {row.logo_url ? (
                          <img src={row.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
                        ) : null}
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--aps-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.name}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--aps-text-muted)' }}>
                            {row.emailCount === 1 ? '1 coach' : `${row.emailCount} coaches`}
                          </span>
                        </span>
                        {note && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--aps-text-muted)', whiteSpace: 'nowrap' }}>
                            {locked && <Lock size={12} aria-hidden />}
                            {note}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {state.isPaid && rows.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--aps-text-muted)' }}>
                    {selected.size} of {rows.length} schools picked
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={working}
                    onClick={() => setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((r) => r.school_id)))}
                  >
                    {selected.size === rows.length ? 'Clear all' : 'Select all'}
                  </Button>
                </div>
              )}
              {coachCount > BIG_SEND && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--aps-text-2)' }}>{programsBigSendLine(coachCount)}</p>
              )}
              {noEmailLine && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--aps-text-muted)' }}>{noEmailLine}</p>
              )}
              {outOfPrograms && rows.some((r) => costsProgram(r.school_id, state)) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                  <p style={{ margin: 0, flex: '1 1 220px', fontSize: 13, color: 'var(--aps-text-2)' }}>{PROGRAMS_OUT}</p>
                  <Button variant="secondary" size="sm" onClick={() => showUpgrade('Email coaches at more schools')}>
                    See plans
                  </Button>
                </div>
              )}
              {!gmailConnected && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--aps-text-2)' }}>{PROGRAMS_GMAIL}</p>
              )}
              {error && (
                <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--aps-danger)' }}>{error}</p>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={onClose} disabled={working}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={write}
                  loading={working}
                  disabled={gmailConnected && selected.size === 0}
                >
                  {gmailConnected ? programsButton(coachCount) : 'Connect Gmail'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
      <SpendConfirm />
    </>
  );
};

export default EmailProgramsModal;
